import { useMemo } from "react"
import { ProgressStatCard } from "@/components/ui/progress-stat-card"
import { Star, Target, MapPin } from "lucide-react"
import { StudentStore } from "@/hooks/stores/StudentStore"
import { StorageService } from "@/hooks/services/storageService"
import { AuthStore } from "@/hooks/stores/AuthStore"

function useGoalStats() {
  const userId = AuthStore((s) => s.currentUser?._id)
  return useMemo(() => {
    if (!userId) return { completed: 0, active: 0 }
    const all = StorageService.read("goals")
    if (!Array.isArray(all)) return { completed: 0, active: 0 }
    const mine = all.filter((g: any) => g.userId === userId)
    return {
      completed: mine.filter((g: any) => g.status === "completed" || g.progress >= 100).length,
      active: mine.filter((g: any) => g.status !== "completed" && (g.progress ?? 0) < 100).length,
    }
  }, [userId])
}

function useDailyChallengeXp() {
  return useMemo(() => {
    try {
      const history: Record<string, string> = JSON.parse(
        localStorage.getItem("daily-challenge-history") ?? "{}"
      )
      return Object.values(history).reduce<number>(
        (sum, v) => sum + (v === "correct" ? 25 : v === "wrong" ? 5 : 0),
        0
      )
    } catch {
      return 0
    }
  }, [])
}

export function ProgressStats() {
  const student = StudentStore((s) => s.student)
  const { completed } = useGoalStats()
  const challengeXp = useDailyChallengeXp()

  const xpPoints = useMemo(() => {
    const fromAssessment = student?.traitProfile ? 50 : 0
    const fromEvents = ((student?.traitEvents as any[]) ?? []).length * 10
    const fromActivity = ((student?.activityLog as any[]) ?? []).length * 5
    return fromAssessment + fromEvents + fromActivity + challengeXp
  }, [student, challengeXp])

  const pathCount = student?.selectedBroadPaths?.length ?? 0

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
      <ProgressStatCard
        icon={<Star className="w-8 h-8 text-amber-600" />}
        label="XP Earned"
        value={xpPoints.toLocaleString()}
        iconBgColor="bg-amber-100"
      />
      <ProgressStatCard
        icon={<Target className="w-8 h-8 text-indigo-600" />}
        label="Goals Completed"
        value={completed.toString()}
        iconBgColor="bg-indigo-100"
      />
      <ProgressStatCard
        icon={<MapPin className="w-8 h-8 text-emerald-600" />}
        label="Career Paths"
        value={pathCount.toString()}
        iconBgColor="bg-emerald-100"
      />
    </div>
  )
}
